import { getEnv } from '../env';

import { apiClient, getAccessToken, handleUnauthorized } from './axios';

import type { ChatListResponse, ChatMessagesResponse, SSEEvent } from '@repo/shared-types';

export async function streamChatMessage(
  message: string,
  chatId: string | undefined,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal,
  _retried = false,
): Promise<void> {
  const baseUrl = getEnv('VITE_API_URL');
  const token = getAccessToken();

  const response = await fetch(`${baseUrl}/chats/send-message-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ message, chat_id: chatId }),
    signal,
  });

  if (response.status === 401 && !_retried) {
    const refreshed = await handleUnauthorized();
    if (refreshed) {
      return streamChatMessage(message, chatId, onEvent, signal, true);
    }
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as { message?: string }).message || `HTTP error ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const dataStr = line.slice(6).trim();
          if (dataStr) {
            try {
              const event = JSON.parse(dataStr) as SSEEvent;
              onEvent(event);
            } catch {
              // skip malformed SSE lines
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function getChatMessages(chatId: string): Promise<ChatMessagesResponse> {
  const response = await apiClient.get<ChatMessagesResponse>(`/chats/${chatId}/messages`);
  return response.data;
}

export async function listChats(page = 1, limit = 20): Promise<ChatListResponse> {
  const response = await apiClient.get<ChatListResponse>('/chats', { params: { page, limit } });
  return response.data;
}

export async function deleteChat(chatId: string): Promise<void> {
  await apiClient.delete(`/chats/${chatId}`);
}
